import { FeatureFlag } from '@ps/web-core-enums';
import { isNil, omitBy } from 'lodash';

import type { WidgetEffectParams } from '@swf/core/store';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorCode from '@swf/sports/utils/getErrorCode';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type {
  MatchFiltersApiSuccessResponse,
  MatchFiltersRequestedPayload,
  WidgetDispatch,
  WidgetSelect
} from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, rootState, select, widget } = params;
    const { matchFiltersRequested, clear }: WidgetDispatch = dispatch[widget.uid];
    const { hasPreloaded }: WidgetSelect = select[widget.uid];

    sportsWidgetPollingBehavior(params).subscribe({
      next: (pollingSource) =>
        matchFiltersRequested({
          polling: pollingSource === 'INTERVAL',
          isPageChange: pollingSource === 'PAGE_CHANGE'
        }),
      complete: clear
    });

    if (!hasPreloaded(rootState)) {
      await matchFiltersRequested();
    }
  },
  async matchFiltersRequested(
    payload: MatchFiltersRequestedPayload = {},
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ): Promise<void> {
    const { dispatch, request, widget, select, rootState } = params;
    const { eventTypeId } = select.sports.getUrlParams(rootState);
    const { filterSearchParams }: WidgetSelect = select[widget.uid];
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const sportsRequest = buildSportsRequest(request, rootState);
    const queryParams = filterSearchParams(rootState);

    const filters = {
      ...queryParams,
      ...payload
    };

    try {
      const { body } = await sportsRequest<MatchFiltersApiSuccessResponse>({
        path: `/match-filter/${eventTypeId}`,
        body: omitBy(filters, isNil),
        includeFeatures: [
          FeatureFlag.ISPEnableStarSub,
          FeatureFlag.ISPEnableLiveStreaming,
          FeatureFlag.ISPEnableMarketMatchOddsTwoUp,
          FeatureFlag.ISPEnableMarketMatchOddsNinety
        ],
        signal: getWidgetCancellationSignal(params)
      });
      widgetDispatch.dataLoadedSuccessfully(body);
    } catch (error) {
      if (hasRequestAborted(error)) return;
      widgetDispatch.dataLoadFailed(getErrorCode(error));
    }

    return;
  }
};
